import { useCallback, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, List, ListItemButton, ListItemText, Stack,
  MenuItem, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";

import { useAuthContext } from "src/auth/hooks";
import { useTranslate } from "src/locales";
import { usePathname, useRouter } from "src/routes/hooks";
import { useResponsive } from "src/hooks/use-responsive";
import { getProviderOnboardingSteps } from "src/sections/sportprovider/common/provider-onboarding";
import { onboardingMediaUrl, useOnboardingAccess, useOnboardingCatalog, useOnboardingContent } from "src/api/onboarding-content";
import Iconify from "src/components/iconify";
import type { OnboardingContext, OnboardingFormat, OnboardingLength } from "src/types/onboarding-content";
import type { ProductContentLocale } from "src/types/product-content";

import AdminHelpDialog, { AdminHelpContent } from "./admin-help-dialog";
import { onboardingStorageKey, readOnboardingState, writeOnboardingState, type OnboardingState } from "./admin-onboarding-state";

const formats: { value: OnboardingFormat; icon: string }[] = [
  { value: "text", icon: "solar:document-text-bold-duotone" },
  { value: "images", icon: "solar:gallery-bold-duotone" },
  { value: "video", icon: "solar:videocamera-record-bold-duotone" },
];

function contentLocale(language: string): ProductContentLocale {
  const code = language.split("-")[0];
  return ["bg", "en", "fr", "ger", "gre", "hu", "srb"].includes(code) ? code as ProductContentLocale : "en";
}

function Markdown({ children }: { children: string }) {
  return <Box sx={{ overflowWrap: "anywhere", "& h1": { typography: "h5" }, "& h2": { typography: "h6" }, "& a": { color: "primary.main" }, "& li": { mb: 1 }, "& p:first-of-type": { mt: 0 } }}>
    <ReactMarkdown remarkPlugins={[remarkGfm]}>{children}</ReactMarkdown>
  </Box>;
}

function GuideImage({ src, alt }: { src: string; alt: string }) {
  const { t } = useTranslate();
  const [failed, setFailed] = useState(false);
  return failed ? <Alert severity="warning">{t("admin_onboarding_media_error")}</Alert> :
    <Box component="a" href={onboardingMediaUrl(src)} target="_blank" rel="noopener noreferrer" aria-label={t("admin_onboarding_image_open")} sx={{ display: "block" }}>
      <Box component="img" src={onboardingMediaUrl(src)} alt={alt} loading="lazy" onError={() => setFailed(true)}
        sx={{ width: 1, height: "auto", borderRadius: 1.5, border: 1, borderColor: "divider" }} />
    </Box>;
}

type GuideVideoProps = { src: string; poster: string; captions: string; title: string; locale: "hu" | "en"; active: boolean };
function GuideVideo({ src, poster, captions, title, locale, active }: GuideVideoProps) {
  const { t } = useTranslate();
  const ref = useRef<HTMLVideoElement>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    const element = ref.current;
    if (!active) element?.pause();
    return () => { element?.pause(); };
  }, [active]);
  return failed ? <Alert severity="warning">{t("admin_onboarding_media_error")}</Alert> :
    <Box component="video" ref={ref} controls playsInline crossOrigin="anonymous" preload="none" src={onboardingMediaUrl(src)}
      poster={onboardingMediaUrl(poster)} aria-label={title} onError={() => setFailed(true)}
      sx={{ width: 1, borderRadius: 1.5, bgcolor: "background.neutral", maxHeight: "65vh" }}>
      <track kind="captions" src={onboardingMediaUrl(captions)} srcLang={locale} label={locale === "hu" ? "Magyar" : "English"} default />
      {t("admin_onboarding_media_error")}
    </Box>;
}

type EntryProps = { providerName?: string; providerReady?: boolean; request?: number; hideTrigger?: boolean };
export default function AdminOnboardingEntry({ providerName, providerReady = true, request = 0, hideTrigger = false }: EntryProps) {
  const { user, currentProvider, currentProviderType, loading } = useAuthContext();
  if (!user?.id || !currentProvider || !["facility", "coach", "organiser"].includes(currentProviderType)) {
    return <AdminHelpDialog section="admin_help_section_admin_start_steps" request={request} hideTrigger={hideTrigger} />;
  }
  return <AdminOnboardingDialog key={`${user.id}:${currentProvider}`} userId={Number(user.id)} providerId={currentProvider}
    context={currentProviderType as OnboardingContext} providerName={providerName} request={request} hideTrigger={hideTrigger} providerReady={providerReady && !loading} />;
}

type Props = { userId: number; providerId: number; context: OnboardingContext; providerName?: string; request?: number; hideTrigger?: boolean; providerReady?: boolean };
export function AdminOnboardingDialog({ userId, providerId, context, providerName, request = 0, hideTrigger = false, providerReady = true }: Props) {
  const { t, i18n } = useTranslate();
  const pathname = usePathname();
  const router = useRouter();
  const fullScreen = useResponsive("down", "md");
  const storageKey = onboardingStorageKey(userId, providerId);
  const [state, setState] = useState(() => readOnboardingState(storageKey));
  const [open, setOpen] = useState(false);
  const [legacy, setLegacy] = useState(false);
  const previousRequest = useRef(request);
  const locale = contentLocale(i18n.language);
  const catalog = useOnboardingCatalog(context, locale);
  const openManually = useCallback(() => {
    setLegacy(false);
    setOpen(true);
    void Promise.resolve(catalog.mutate()).catch(() => undefined);
  }, [catalog.mutate]);
  useEffect(() => {
    if (request !== previousRequest.current) {
      previousRequest.current = request;
      openManually();
    }
  }, [request, openManually]);
  const topics = catalog.data?.topics ?? [];
  const topic = topics.find((item) => item.id === state.lastTopicId);
  const guide = useOnboardingContent(context, locale, topic?.id ?? null, state.format, state.textLength, open && !legacy);
  const access = useOnboardingAccess(userId, providerId, providerReady && topics.length > 0);
  const steps = getProviderOnboardingSteps({ profile: false, images: false, sports: false, venues: false, global_settings: false, services: false, tournaments: false }, context);
  const destination = steps.find((step) => step.id === topic?.action)?.href;
  const canContinue = providerReady && destination && !access.error && access.data?.provider_id === providerId && access.data.actions.includes(topic?.action ?? "");

  const save = useCallback((patch: Partial<OnboardingState>) => {
    setState((previous) => {
      const next = { ...previous, ...patch };
      writeOnboardingState(storageKey, next);
      return next;
    });
  }, [storageKey]);

  const close = () => { if (topics.length) save({ dismissed: true }); setOpen(false); };

  useEffect(() => {
    if (!providerReady || state.dismissed || open || !topics.length || access.error || access.data?.provider_id !== providerId || pathname.replace(/\/$/, "") !== "/dashboard") return undefined;
    const show = () => {
      if (!document.querySelector('[role="dialog"], [role="alertdialog"]')) {
        setOpen(true);
      }
    };
    const observer = new MutationObserver(show);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["role"] });
    const timer = window.setTimeout(show, 0);
    return () => { window.clearTimeout(timer); observer.disconnect(); };
  }, [open, pathname, state.dismissed, topics.length, access.data?.provider_id, access.error, providerId, providerReady]);

  const loading = catalog.isLoading || (Boolean(topic && state.format) && guide.isLoading);
  const failed = catalog.error || guide.error;
  return <>
    {!hideTrigger && <Tooltip title={t("help")}>
      <IconButton aria-label={t("help")} onClick={openManually} sx={{ color: "info.main", pt: 0.5 }}>
        <Iconify icon="mingcute:question-line" />
      </IconButton>
    </Tooltip>}
    <Dialog open={open} onClose={close} fullScreen={fullScreen} fullWidth maxWidth="lg" aria-labelledby="admin-onboarding-title"
      PaperProps={{ sx: { height: { md: "min(850px, 90dvh)" }, borderRadius: { xs: 0, md: 2 } } }}>
      <DialogTitle sx={{ pb: 2, borderBottom: 1, borderColor: "divider" }}>
        <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between">
          <Box sx={{ minWidth: 0 }}>
            <Typography id="admin-onboarding-title" variant="h5" component="h2">{t(legacy ? "admin_onboarding_more_help" : "admin_onboarding_title")}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, overflowWrap: "anywhere" }}>
              {providerName ? `${providerName} · ` : ""}{t(`admin_onboarding_type_${context}`)}
            </Typography>
          </Box>
          <IconButton aria-label={t("admin_onboarding_close")} onClick={close}><Iconify icon="mingcute:close-line" /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ p: { xs: 2, md: 3 } }}>
        {legacy ? <AdminHelpContent section="admin_help_section_admin_start_steps" /> : <Stack direction={{ xs: "column", md: "row" }} spacing={3} sx={{ pt: 2, minHeight: 0 }}>
          <Box component="nav" aria-label={t("admin_onboarding_topics")} sx={{ width: { md: 235 }, flexShrink: 0 }}>
            {fullScreen ? <TextField select fullWidth disabled={!topics.length} label={t("admin_onboarding_topics")} value={topic?.id ?? ""}
              SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}
              onChange={(event) => save({ lastTopicId: event.target.value })}>
              <MenuItem value="" disabled>{t("admin_onboarding_welcome")}</MenuItem>
              {topics.map((item) => <MenuItem key={item.id} value={item.id}>{item.title}</MenuItem>)}
            </TextField> : <>
            <Typography variant="overline" color="text.secondary">{t("admin_onboarding_topics")}</Typography>
            <List disablePadding>
              {topics.map((item) => <ListItemButton key={item.id} selected={topic?.id === item.id} onClick={() => save({ lastTopicId: item.id })}
                sx={{ borderRadius: 1, px: 1.5, minWidth: 0, flexGrow: { xs: 1, md: 0 }, "&.Mui-selected": { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1) } }}>
                <ListItemText primary={item.title} primaryTypographyProps={{ variant: "body2", fontWeight: topic?.id === item.id ? 600 : 400 }} />
              </ListItemButton>)}
            </List>
            </>}
          </Box>
          <Stack spacing={2.5} sx={{ flex: 1, minWidth: 0 }}>
            <Box>
              <Typography variant="h6">{topic?.title ?? t("admin_onboarding_welcome")}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{topic?.summary ?? t("admin_onboarding_intro")}</Typography>
            </Box>
            {(guide.data?.used_fallback || catalog.data?.used_fallback) && <Alert severity="info">{t("admin_onboarding_english_fallback")}</Alert>}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>{t("admin_onboarding_choose_format")}</Typography>
              <ToggleButtonGroup exclusive disabled={!topics.length} color="primary" value={state.format} aria-label={t("admin_onboarding_choose_format")}
                onChange={(_, value: OnboardingFormat | null) => { if (value) save({ format: value }); }}
                sx={{ display: "flex", flexWrap: "wrap", gap: 1, "& .MuiToggleButtonGroup-grouped": { border: 1, borderColor: "divider", borderRadius: "8px !important", m: "0 !important", flex: "1 1 110px", textTransform: "none", px: 1.5 } }}>
                {formats.map((format) => <ToggleButton key={format.value} value={format.value} aria-label={t(`admin_onboarding_format_${format.value}`)}>
                  <Stack spacing={0.5} alignItems="center"><Iconify icon={format.icon} width={24} /><span>{t(`admin_onboarding_format_${format.value}`)}</span></Stack>
                </ToggleButton>)}
              </ToggleButtonGroup>
            </Box>
            {state.format === "text" && <ToggleButtonGroup exclusive color="primary" size="small" value={state.textLength} aria-label={t("admin_onboarding_text_length")}
              onChange={(_, value: OnboardingLength | null) => { if (value) save({ textLength: value }); }}>
              <ToggleButton value="short">{t("admin_onboarding_short")}</ToggleButton><ToggleButton value="detailed">{t("admin_onboarding_detailed")}</ToggleButton>
            </ToggleButtonGroup>}
            <Box aria-live="polite" aria-busy={loading}>
              {loading && <CircularProgress size={28} aria-label={t("admin_onboarding_loading")} />}
              {failed && <Alert severity="error" action={<Button onClick={() => { void Promise.allSettled([catalog.mutate(), guide.mutate()]); }}>{t("admin_onboarding_retry")}</Button>}>{t("admin_onboarding_error")}</Alert>}
              {!loading && !failed && !topics.length && <Alert severity="info">{t("admin_onboarding_unavailable")}</Alert>}
              {!loading && !failed && topics.length > 0 && (!topic || !state.format) && <Alert severity="info">{t("admin_onboarding_choose_topic")}</Alert>}
              {!loading && !failed && guide.data && topic && state.format && <Stack spacing={2}>
                {guide.data.text && <Markdown>{guide.data.text}</Markdown>}
                {guide.data.images.map((image, index) => <Box component="figure" key={image.src} sx={{ m: 0 }}>
                  <Typography component="figcaption" variant="subtitle2" sx={{ mb: 1 }}>{index + 1}. {image.caption}</Typography>
                  <GuideImage key={image.src} src={image.src} alt={image.alt} />
                </Box>)}
                {guide.data.video && <>
                  <GuideVideo key={guide.data.video.src} {...guide.data.video} title={topic.title} locale={guide.data.locale} active={open && !legacy} />
                  <Typography variant="subtitle2">{t("admin_onboarding_transcript")}</Typography>
                  <Markdown>{guide.data.video.transcript}</Markdown>
                </>}
              </Stack>}
            </Box>
          </Stack>
        </Stack>}
      </DialogContent>
      <DialogActions sx={{ borderTop: 1, borderColor: "divider", p: 2, flexWrap: "wrap", gap: 1, justifyContent: "space-between" }}>
        <Button onClick={() => setLegacy(!legacy)} startIcon={<Iconify icon={legacy ? "solar:arrow-left-linear" : "solar:question-circle-linear"} />}>
          {t(legacy ? "admin_onboarding_back" : "admin_onboarding_more_help")}
        </Button>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {!legacy && canContinue && <Button variant="contained" color="primary" onClick={() => { close(); router.push(destination); }}>{t("admin_onboarding_open_setting")}</Button>}
          <Button onClick={close}>{t("admin_onboarding_close")}</Button>
        </Stack>
      </DialogActions>
    </Dialog>
  </>;
}
